import { Checkbox, Dropdown, message, Tooltip, Modal, Image, Empty, Button, Popconfirm } from "antd";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    User,
    Phone,
    CheckCircle,
    RefreshCcw,
    AlertTriangle,
    Copy,
    ExternalLink,
    CalendarDays,
    UserPlus,
    UserPen,
} from "lucide-react";

import ReportDropdown from "./ReportDropdown"
import { getChannelCustomerDetail, getCustomerDetail, updateChannelCustomer, getChannelCustomerSpamLogs, markSpamChannelCustomer, CustomerSpam } from "../../../api/ChannelCustomer";
import { getChannelMessages } from "../../../api/ChannelMessage";
import type { AdInfo } from "../../../api/ChannelMessage";

// Quảng cáo kèm thời điểm khách tương tác (lấy từ ad_histories)
type AdEntry = AdInfo & { interacted_at?: string };

// ad_json có thể là object hoặc chuỗi JSON tuỳ bản ghi -> chuẩn hoá về object
const parseAdJson = (value: any): AdInfo => {
    if (!value) return {};
    if (typeof value === "string") {
        try { return JSON.parse(value) || {}; } catch { return {}; }
    }
    return typeof value === "object" ? value : {};
};

// Thời điểm khách vào từ quảng cáo: dd/MM/yyyy HH:mm
const formatAdTime = (value?: string): string => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Gộp nhiều nguồn quảng cáo về 1 danh sách: ad_histories (mọi lần khách vào từ quảng cáo)
// + ad_info/ad_json (quảng cáo mới nhất). Bỏ trùng theo ad_id, mới nhất lên đầu.
const buildAdList = (d: any): AdEntry[] => {
    const list: AdEntry[] = [];
    const seen = new Set<string>();

    const add = (ad: AdInfo, interactedAt?: string) => {
        if (!ad || (!ad.ad_id && !ad.ad_name && !ad.creative_name && !ad.post_title)) return;
        const key = String(ad.ad_id || ad.ad_name || ad.creative_name || ad.post_title);
        const existed = seen.has(key);
        if (existed) {
            // Cùng quảng cáo: giữ bản ghi cũ nhưng bù các trường còn thiếu
            const current = list.find(x => String(x.ad_id || x.ad_name || x.creative_name || x.post_title) === key);
            if (current) {
                Object.entries(ad).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== "" && (current as any)[k] == null) (current as any)[k] = v;
                });
                if (!current.interacted_at && interactedAt) current.interacted_at = interactedAt;
            }
            return;
        }
        seen.add(key);
        list.push({ ...ad, interacted_at: interactedAt || ad.interacted_at });
    };

    const histories = Array.isArray(d?.ad_histories) ? [...d.ad_histories] : [];
    // Mới nhất lên đầu (API có thể trả thứ tự bất kỳ)
    histories.sort((a: any, b: any) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
    histories.forEach((h: any) => {
        const ad = parseAdJson(h?.ad_json);
        add({ ad_id: h?.ad_id || ad.ad_id, ...ad }, h?.created_at);
    });

    // Quảng cáo mới nhất trên bản ghi khách: bổ sung nếu chưa có trong lịch sử
    const latest = { ...(d?.ad_info || {}), ...parseAdJson(d?.ad_json) } as AdInfo;
    if (!latest.ad_id && d?.ad_id) latest.ad_id = d.ad_id;
    add(latest);

    return list;
};

export default function CustomerDropdown({ isDirty, isOrderDirty, onUpdateCustomer, onCreateAppointment, customerId, crmCustomerId, updatingCustomer, creatingAppointment, canEditCustomer, canCreateAppointment, isNewCustomer }: { isDirty?: boolean; isOrderDirty?: boolean; onUpdateCustomer?: () => void; onCreateAppointment?: () => void; customerId?: number; crmCustomerId?: number | null; updatingCustomer?: boolean; creatingAppointment?: boolean; canEditCustomer?: boolean; canCreateAppointment?: boolean; isNewCustomer?: boolean }) {
    const { t } = useTranslation();
    const [isPriority, setIsPriority] = useState(false);
    // Thông tin hiển thị: đã tạo khách -> lấy từ CRM customer; chưa tạo -> lấy từ khách kênh chat
    const [info, setInfo] = useState<{ name: string; phone: string; avatar: string }>({ name: "", phone: "", avatar: "" });
    const [avatarError, setAvatarError] = useState(false);
    // Tương tác quảng cáo: khách có thể vào từ NHIỀU quảng cáo -> lấy cả ad_histories,
    // ad_info/ad_json chỉ là quảng cáo mới nhất nên dùng làm nguồn dự phòng.
    const [adList, setAdList] = useState<AdEntry[]>([]);
    // Ảnh lỗi theo từng quảng cáo (key = ad_id hoặc vị trí trong danh sách)
    const [adImgError, setAdImgError] = useState<Record<string, boolean>>({});
    // Quảng cáo mới nhất -> hiển thị ở nhãn thu gọn
    const adInfo = adList[0] || null;
    // Thống kê đã đọc / chưa đọc: đã đọc = tổng tin nhắn - chưa đọc
    const [stats, setStats] = useState<{ total: number; unread: number; spam: number }>({ total: 0, unread: 0, spam: 0 });
    const readCount = Math.max(0, stats.total - stats.unread);
    // Lịch sử spam của khách hàng
    const [spamLogs, setSpamLogs] = useState<CustomerSpam[]>([]);
    const [openSpamModal, setOpenSpamModal] = useState(false);
    // Cờ đang bị đánh dấu spam (tách khỏi số lượng log lịch sử)
    const [isSpamFlag, setIsSpamFlag] = useState(false);
    const [unmarking, setUnmarking] = useState(false);
    const phone = info.phone;

    // Tải lại lịch sử spam (dùng khi mở khách và sau khi báo xấu để số/danh sách cập nhật ngay)
    const refreshSpamLogs = useCallback(async () => {
        if (!customerId) return;
        const res = await getChannelCustomerSpamLogs(customerId);
        const logs = res.status ? res.data : [];
        setSpamLogs(logs);
        setStats(prev => ({ ...prev, spam: logs.length }));
    }, [customerId]);

    // Gỡ spam: cập nhật cờ is_spam = 0 (backend xóa toàn bộ lịch sử log)
    const handleUnmarkSpam = async () => {
        if (!customerId) return;
        setUnmarking(true);
        const res = await markSpamChannelCustomer(customerId, { is_spam: 0 });
        setUnmarking(false);
        if (res.status) {
            setIsSpamFlag(false);
            // Lấy lại danh sách để số/lịch sử đúng (gỡ spam xóa hết log trước đó)
            await refreshSpamLogs();
            message.success(t("roomchat.spam_unmark_success") || "Đã gỡ spam khách hàng");
        } else {
            message.error(t("roomchat.spam_unmark_error") || "Gỡ spam thất bại");
        }
    };

    // Reset trạng thái lỗi ảnh khi đổi avatar
    useEffect(() => { setAvatarError(false); }, [info.avatar]);
    useEffect(() => { setAdImgError({}); }, [adList]);

    useEffect(() => {
        if (!customerId) return;
        let active = true;

        // Luôn lấy chi tiết khách kênh (cho trạng thái ưu tiên; và là nguồn khi chưa tạo CRM)
        const loadChannel = () => {
            getChannelCustomerDetail(customerId).then(res => {
                if (!active || !res.status || !res.data) return;
                const d: any = res.data;
                setIsPriority(!!d.is_priority_contact);
                // Tương tác quảng cáo: ưu tiên ad_histories (đủ mọi quảng cáo khách từng vào),
                // ad_info/ad_json chỉ là quảng cáo mới nhất -> dùng bổ sung.
                setAdList(buildAdList(d));
                // Chưa đọc từ chi tiết khách hàng kênh
                setStats(prev => ({ ...prev, unread: Number(d.unread_count) || 0 }));
                // Cờ spam hiện tại
                setIsSpamFlag(Number(d.is_spam) === 1);
                if (!crmCustomerId) {
                    setInfo({ name: d.display_name || "", phone: d.phone || "", avatar: d.avatar || d.avatar_url || "" });
                }
            });
            // Tổng tin nhắn của hội thoại (để tính đã đọc = tổng - chưa đọc)
            getChannelMessages({ customer_id: customerId }).then(res => {
                if (!active || !res.status || !res.data) return;
                setStats(prev => ({ ...prev, total: Number(res.data!.total) || 0 }));
            });
            // Lịch sử spam -> số lượng cảnh báo spam chính xác
            refreshSpamLogs();
        };
        loadChannel();

        // Đã tạo khách hàng -> ưu tiên thông tin từ hồ sơ CRM
        if (crmCustomerId) {
            getCustomerDetail(crmCustomerId).then(res => {
                if (!active || !res.status || !res.data) return;
                const c: any = res.data;
                setInfo({ name: c.display_name || "", phone: c.phone || "", avatar: c.avatar || c.avatar_url || "" });
            });
        }

        // Khôi phục tin chưa đọc -> nạp lại thống kê
        const onRestored = () => loadChannel();
        window.addEventListener("conversation-unread-restored", onRestored);

        return () => { active = false; window.removeEventListener("conversation-unread-restored", onRestored); };
    }, [customerId, crmCustomerId, refreshSpamLogs]);

    const handlePriorityChange = async (checked: boolean) => {
        if (!customerId) return;
        setIsPriority(checked);
        const res = await updateChannelCustomer(customerId, { is_priority_contact: checked ? 1 : 0 } as any);
        if (res.status) {
            message.success(t("roomchat.update_success") || "Cập nhật thành công");
        } else {
            setIsPriority(!checked);
            message.error(t("roomchat.update_failed") || "Cập nhật thất bại");
        }
    };

    const handleCopy = async (text: string) => {
        await navigator.clipboard.writeText(text);
        message.success(t("roomchat.copied"));
    };

    const handleCall = () => {
        window.location.href = `tel:${phone}`;
    };


    const overlay = (
        <div
            className="
                w-[340px] rounded-xl border shadow-xl overflow-hidden
                bg-white text-gray-800
                dark:bg-[#1f2937] dark:text-gray-100 dark:border-gray-700
            "
        >
            {/* Header */}
            <div className="p-4 border-b dark:border-gray-700">
                <div className="flex gap-3">
                    {info.avatar && !avatarError ? (
                        <img
                            src={info.avatar}
                            onError={() => setAvatarError(true)}
                            className="w-11 h-11 rounded-full object-cover"
                            alt=""
                        />
                    ) : (
                        <div className="w-11 h-11 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center font-semibold text-gray-500 dark:text-gray-300">
                            {(info.name || "?").charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t("roomchat.customer")}
                        </div>

                        <div className="font-semibold text-[15px]">
                            {info.name || "—"}
                        </div>

                        <div className="text-xs text-gray-400 mt-1">
                            * {t("roomchat.copy_hint")}
                        </div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 text-sm">
                {/* Thông tin chung */}
                <div className="flex items-center gap-2 font-medium">
                    <User size={14} />
                    {t("roomchat.general_info")}

                    {/* icon copy nhỏ */}
                    <Copy
                        size={14}
                        className="ml-1 cursor-pointer text-gray-400 hover:text-blue-500"
                        onClick={() => handleCopy(info.name)}
                    />
                </div>

                <div className="pl-5 space-y-2 text-gray-600 dark:text-gray-300">
                    <div>
                        <span className="text-gray-500">{t("roomchat.customer_name_short")}:</span>{" "}
                        {info.name || "—"}
                    </div>

                    {/* Phone */}
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500">{t("roomchat.phone_short")}:</span>

                        {phone ? (
                            <span
                                onClick={() => handleCopy(phone)}
                                onDoubleClick={handleCall}
                                className="
                                    bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full
                                    cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600
                                    transition
                                "
                            >
                                {phone}
                            </span>
                        ) : (
                            <span className="text-gray-400">—</span>
                        )}
                    </div>
                </div>

                {/* Calls */}
                <div className="flex items-center gap-2 font-medium pt-2">
                    <Phone size={14} className="text-green-500" />
                    {t("roomchat.recent_calls")}
                </div>

                <div className="pl-5 text-gray-500 dark:text-gray-400">
                    {t("roomchat.no_calls")}
                </div>

                {/* Ads */}
                <div className="pt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                        {t("roomchat.ad_interaction")}
                        {adList.length > 1 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {adList.length}
                            </span>
                        )}
                    </div>

                    {adList.length > 0 ? (
                        // Khách có thể vào từ nhiều quảng cáo -> liệt kê hết, mới nhất lên đầu
                        <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto pr-1">
                            {adList.map((ad, idx) => {
                                const adKey = String(ad.ad_id || ad.ad_name || idx);
                                return (
                                    <div
                                        key={adKey}
                                        className="
                                            flex items-start gap-3 p-2 rounded-xl border
                                            bg-gray-50 dark:bg-gray-800 dark:border-gray-700
                                        "
                                    >
                                        {ad.ad_image && !adImgError[adKey] ? (
                                            <img
                                                src={ad.ad_image}
                                                onError={() => setAdImgError(prev => ({ ...prev, [adKey]: true }))}
                                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                                alt=""
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center">
                                                <ExternalLink size={16} className="text-gray-400" />
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            {/* title + status */}
                                            <div className="flex items-center gap-1 font-medium text-sm">
                                                <span className="truncate" title={ad.ad_name || ""}>
                                                    {ad.ad_name || ad.creative_name || t("roomchat.ad_interaction")}
                                                </span>
                                                {ad.ad_status && (
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${ad.ad_status === "ACTIVE"
                                                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                                                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                                        }`}>
                                                        {ad.ad_status}
                                                    </span>
                                                )}
                                            </div>

                                            {/* tiêu đề bài viết */}
                                            {ad.post_title && (
                                                <div className="text-[12px] text-gray-600 dark:text-gray-300 line-clamp-2 mt-0.5">
                                                    {ad.post_title}
                                                </div>
                                            )}

                                            {/* chiến dịch */}
                                            {ad.campaign_name && (
                                                <div className="text-[10px] text-gray-400 line-clamp-1 mt-0.5" title={ad.campaign_name}>
                                                    {t("roomchat.ad_campaign")}: {ad.campaign_name}
                                                </div>
                                            )}

                                            {/* thời điểm khách vào từ quảng cáo này */}
                                            {ad.interacted_at && (
                                                <div className="text-[10px] text-gray-400 mt-0.5">
                                                    {formatAdTime(ad.interacted_at)}
                                                </div>
                                            )}

                                            {/* id */}
                                            {ad.ad_id && (
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span
                                                        onClick={() => handleCopy(String(ad.ad_id))}
                                                        className="
                                                            text-xs px-2 py-0.5 rounded-full
                                                            bg-gray-200 dark:bg-gray-700
                                                            cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600
                                                            truncate max-w-[170px] inline-block
                                                        "
                                                        title={ad.ad_id}
                                                    >
                                                        {ad.ad_id}
                                                    </span>
                                                </div>
                                            )}

                                            {/* link xem bài viết */}
                                            {ad.post_url && (
                                                <a
                                                    href={ad.post_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline mt-1"
                                                >
                                                    <ExternalLink size={11} />
                                                    {t("roomchat.ad_view_post")}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="pl-1 text-gray-400 dark:text-gray-500 text-xs">
                            {t("roomchat.ad_none")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="trl-w-full-420 flex flex-col justify-between gap-2 fixed bottom-0 right-0 z-10 px-4 py-2 bg-white dark:bg-[#1f1f1f] border-t dark:border-[#2a2a2a] border-l dark:border-[#2a2a2a]">
            <div className="button-check-block border-b dark:border-[#2a2a2a] pb-2">
                {canEditCustomer && (
                    <Checkbox checked={isPriority} onChange={(e) => handlePriorityChange(e.target.checked)}>
                        {t('roomchat.priority_contact')}
                    </Checkbox>
                )}
                {(canEditCustomer || canCreateAppointment) && (
                    <div className="grid grid-cols-2 gap-2">
                        {canEditCustomer && (
                            // Tạo mới khách hàng: hỏi xác nhận để tránh bấm nhầm. Cập nhật thì chạy trực tiếp.
                            <Popconfirm
                                title={t("roomchat.create_customer_confirm_title") || "Tạo khách hàng mới?"}
                                description={t("roomchat.create_customer_confirm_desc") || "Hệ thống sẽ tạo hồ sơ CRM cho khách hàng này."}
                                okText={t("notif.confirm_ok") || "Đồng ý"}
                                cancelText={t("notif.confirm_cancel") || "Hủy"}
                                onConfirm={onUpdateCustomer}
                                disabled={!isNewCustomer || updatingCustomer}
                                placement="topLeft"
                            >
                            <button
                                onClick={isNewCustomer ? undefined : onUpdateCustomer}
                                disabled={updatingCustomer}
                                className={`
                                    mt-2 px-3 py-1 rounded-md
                                    text-white dark:text-white
                                    h-[32px]
                                    text-[12px] font-medium
                                    transition
                                    disabled:opacity-60 disabled:cursor-not-allowed
                                    ${isDirty
                                        ? 'bg-orange-500 text-white ring-2 ring-orange-300 animate-pulse hover:bg-orange-600 dark:bg-orange-600 dark:ring-orange-400'
                                        : 'bg-[#10b981] text-gray-700 hover:bg-[#10b981]/80 dark:bg-green-900/40 dark:text-gray-300'
                                    }
                                `}
                            >
                                {updatingCustomer ? (
                                    <span className="flex items-center gap-1 justify-center">
                                        <svg
                                            className="animate-spin w-3 h-3"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z"
                                            />
                                        </svg>
                                        {t("roomchat.update_customer")}
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 justify-center">
                                        {isNewCustomer ? (
                                            <UserPlus size={14} />
                                        ) : (
                                            <UserPen size={14} />
                                        )}

                                        {isNewCustomer
                                            ? t("roomchat.add_customer") || "Thêm Khách hàng"
                                            : t("roomchat.update_customer")}
                                    </span>
                                )}
                            </button>
                            </Popconfirm>
                        )}
                        {canCreateAppointment && (
                            // Chưa tạo khách hàng (chưa có CRM customer) -> không cho tạo lịch hẹn
                            <Tooltip title={isNewCustomer ? (t('roomchat.create_appointment_need_customer') || 'Vui lòng tạo khách hàng trước khi tạo lịch hẹn') : ''}>
                                <button
                                    onClick={onCreateAppointment}
                                    disabled={creatingAppointment || isNewCustomer}
                                    className={`
                                        mt-2 px-3 py-1 rounded-md
                                        h-[32px]
                                        text-[12px] font-medium
                                        transition
                                        disabled:opacity-60 disabled:cursor-not-allowed
                                        ${isOrderDirty && !isNewCustomer
                                            ? 'bg-orange-500 text-white ring-2 ring-orange-300 animate-pulse hover:bg-orange-600 dark:bg-orange-600 dark:ring-orange-400'
                                            : 'bg-[#3e91ab] text-white hover:bg-[#3e91ab]/80 dark:bg-blue-900/40 dark:text-blue-300'
                                        }
                                    `}
                                >
                                    {creatingAppointment ? (
                                        <span className="flex items-center gap-1 justify-center">
                                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                                            {t('roomchat.create_appointment')}
                                        </span>
                                    ) :
                                        <span className="flex items-center gap-1 justify-center">
                                            <CalendarDays size={14} />
                                            {t('roomchat.create_appointment')}
                                        </span>
                                    }
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-4 justify-between">
                <div className="flex items-center gap-2">
                    <Dropdown
                        dropdownRender={() => overlay}
                        trigger={["click"]}
                        placement="topCenter"
                        arrow
                        getPopupContainer={() => document.body}
                    >
                        <div className="flex items-center gap-2 cursor-pointer">
                            {/* Icon circle */}
                            <div
                                className="
                                    w-6 h-6 flex items-center justify-center rounded-full
                                    bg-green-100 text-green-600
                                    dark:bg-green-900/40 dark:text-green-400
                                "
                            >
                                <Phone size={12} />
                            </div>

                            {/* Label: tên quảng cáo (nếu khách đến từ ad) */}
                            <div
                                className="
                                    px-3 py-1 rounded-full
                                    bg-purple-100 text-purple-700
                                    dark:bg-purple-900/40 dark:text-purple-300
                                    text-[12px] font-medium
                                    truncate max-w-[160px]
                                "
                                title={adInfo?.ad_name || ""}
                            >
                                {adInfo?.ad_name || adInfo?.creative_name || t("roomchat.ad_none")}
                            </div>
                            {/* Khách vào từ nhiều quảng cáo -> báo số lượng còn lại */}
                            {adList.length > 1 && (
                                <div className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-[12px] font-medium flex-shrink-0">
                                    +{adList.length - 1}
                                </div>
                            )}
                        </div>
                    </Dropdown>
                    {/* Stats mini: đã đọc / chưa đọc / cảnh báo */}
                    <div className="flex items-center gap-3 ml-2 text-xs">
                        <Tooltip title={t("roomchat.stats_read") || "Đã đọc"}>
                            <span className="flex items-center gap-1 text-green-500">
                                <CheckCircle size={14} /> {readCount}
                            </span>
                        </Tooltip>

                        <Tooltip title={t("roomchat.stats_unread") || "Chưa đọc"}>
                            <span className="flex items-center gap-1 text-red-500">
                                <RefreshCcw size={14} /> {stats.unread}
                            </span>
                        </Tooltip>

                        {/* Cảnh báo spam: số lượng lấy từ lịch sử spam, click để xem danh sách */}
                        <Tooltip title={t("roomchat.stats_spam") || "Spam"}>
                            <span
                                className={`flex items-center gap-1 text-orange-500 ${stats.spam > 0 ? "cursor-pointer hover:text-orange-600" : ""}`}
                                onClick={() => { if (stats.spam > 0) setOpenSpamModal(true); }}
                            >
                                <AlertTriangle size={14} /> {stats.spam}
                            </span>
                        </Tooltip>
                    </div>
                </div>
                <ReportDropdown customerId={customerId} onReported={() => { refreshSpamLogs(); setIsSpamFlag(true); }} />
            </div>

            {/* Danh sách lịch sử spam của khách hàng */}
            <Modal
                open={openSpamModal}
                onCancel={() => setOpenSpamModal(false)}
                footer={null}
                width={640}
                title={
                    <div className="flex items-center gap-2 text-[#dc2626] font-semibold">
                        <AlertTriangle size={18} />
                        {t("roomchat.spam_history") || "Lịch sử spam"} ({spamLogs.length})
                    </div>
                }
                className="
                    dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-title]:text-white
                "
            >
                {/* Trạng thái spam hiện tại + nút gỡ spam */}
                <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-[#2a2a2a]">
                    <span className="text-[13px]">
                        {t("roomchat.spam_status") || "Trạng thái"}:{" "}
                        {isSpamFlag ? (
                            <span className="font-semibold text-[#dc2626]">
                                {t("roomchat.spam_flagged") || "Đang bị đánh dấu spam"}
                            </span>
                        ) : (
                            <span className="font-semibold text-green-600">
                                {t("roomchat.spam_clean") || "Không bị đánh dấu"}
                            </span>
                        )}
                    </span>
                    {isSpamFlag && (
                        <Popconfirm
                            title={t("roomchat.spam_unmark") || "Gỡ spam"}
                            description={t("roomchat.spam_unmark_confirm") || "Bạn chắc chắn muốn gỡ đánh dấu spam cho khách này?"}
                            okText={t("roomchat.spam_unmark") || "Gỡ spam"}
                            cancelText={t("roomchat.cancel") || "Hủy"}
                            okButtonProps={{ loading: unmarking }}
                            onConfirm={handleUnmarkSpam}
                        >
                            <Button size="small" danger loading={unmarking}>
                                {t("roomchat.spam_unmark") || "Gỡ spam"}
                            </Button>
                        </Popconfirm>
                    )}
                </div>

                {spamLogs.length === 0 ? (
                    <Empty description={t("roomchat.spam_empty") || "Chưa có ghi nhận spam"} />
                ) : (
                    <div className="max-h-[60vh] overflow-y-auto space-y-3">
                        {[...spamLogs]
                            .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                            .map((log) => {
                            const images = [log.image_1, log.image_2, log.image_3].filter(Boolean) as string[];
                            return (
                                <div
                                    key={log.id}
                                    className="rounded-lg border border-[#fee2e2] bg-[#fff7f7] dark:bg-[#2a1f1f] dark:border-[#4b2a2a] p-3"
                                >
                                    <div className="flex items-center justify-between mb-1 text-[12px]">
                                        <span className="font-medium text-slate-700 dark:text-slate-200">
                                            {log.creator?.fullname || "—"}
                                        </span>
                                        <span className="text-slate-400">
                                            {log.created_at
                                                ? new Date(log.created_at).toLocaleString("vi-VN")
                                                : ""}
                                        </span>
                                    </div>
                                    <div className="text-[14px] text-slate-700 dark:text-slate-200 mb-2 whitespace-pre-line">
                                        {log.note || "—"}
                                    </div>
                                    {images.length > 0 && (
                                        <Image.PreviewGroup>
                                            <div className="flex flex-wrap gap-2">
                                                {images.map((src, i) => (
                                                    <Image
                                                        key={i}
                                                        src={src}
                                                        width={90}
                                                        height={90}
                                                        className="rounded-md object-cover"
                                                        style={{ objectFit: "cover" }}
                                                    />
                                                ))}
                                            </div>
                                        </Image.PreviewGroup>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>
        </div>
    );
}